/**
 * i18n Store
 * 管理应用的语言设置
 */

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from "@/i18n";
import { action, makeAutoObservable, observable } from "mobx";

/**
 * i18n Store
 * 负责管理应用的当前语言设置
 */
export class I18nStore {
  @observable locale: Locale = DEFAULT_LOCALE;

  constructor() {
    makeAutoObservable(this);
    this.initLocale();
  }

  /**
   * 初始化语言设置
   * 从以下来源按优先级读取：
   * 1. localStorage
   * 2. 浏览器语言
   * 3. 默认语言 (zh-CN)
   */
  private initLocale() {
    if (typeof window === "undefined") {
      return;
    }

    // 1. 尝试从 localStorage 读取
    const savedLocale = localStorage.getItem("vidmuse:locale") as Locale;
    if (savedLocale && this.isValidLocale(savedLocale)) {
      this.locale = savedLocale;
      return;
    }

    // 2. 尝试从浏览器语言推断
    const browserLang = navigator.language || "";
    if (browserLang.startsWith("zh")) {
      this.locale = "zh-CN";
    } else {
      this.locale = DEFAULT_LOCALE;
    }
  }

  /**
   * 设置当前语言
   * 会自动保存到 localStorage
   */
  @action setLocale(locale: Locale) {
    if (!this.isValidLocale(locale)) {
      console.warn(
        `Invalid locale: ${locale}, falling back to ${DEFAULT_LOCALE}`
      );
      return;
    }

    this.locale = locale;

    // 保存到 localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem("vidmuse:locale", locale);
    }
  }

  /**
   * 检查语言代码是否有效
   */
  private isValidLocale(locale: string): locale is Locale {
    return SUPPORTED_LOCALES.includes(locale as Locale);
  }
}

/**
 * 全局 i18n store 实例
 */
export const i18nStore = new I18nStore();
